import axios from 'axios';
import { datadogRum } from '@datadog/browser-rum';
import { type HttpServiceConfigurator } from './http-service-configurator';
import HttpError from './HttpError';

declare global {
  interface Window {
    activeHttpCount: number;
  }
}

window.activeHttpCount = 0;

const incrementHttpCounter = () => {
  window.activeHttpCount++;
};

const decrementHttpCounter = () => {
  window.activeHttpCount--;
};

export const createHttpService = (
  httpServiceConfigurator: HttpServiceConfigurator
) => {
  const httpServiceInstance = axios.create(
    httpServiceConfigurator.getHttpClientConfig()
  );

  httpServiceInstance.interceptors.request.use(
    async (config) => {

      incrementHttpCounter();
      return await httpServiceConfigurator.prepareRequestConfig(config);
    }
  );

  httpServiceInstance.interceptors.response.use(
    (response) => {
      decrementHttpCounter();
      const isBlob = response?.data instanceof Blob;
      const isEmpty = response?.data === null
        || response?.data === undefined
        || response?.data === '';

      const isStringResponse = typeof response.data === 'string';

      if (!isBlob && !isEmpty && isStringResponse) {
        datadogRum.addError('Unexpected string response', {
          url: response.config.url,
          method: response.config.method,
          requestParams: response.config.params,
          requestData: response.config.data,
          responseData: response.data,
          headers: response.headers,
          contentType: response.headers['content-type'],
          contentEncoding: response.headers['content-encoding'],
        });

        return Promise.reject(new HttpError({
          response: {
            status: 400,
            statusText: 'Response decoding error',
            data: {
              message: 'Unexpected server response format, please try again or reload the page.',
            }
          }
        } as any));
      }
      return httpServiceConfigurator.prepareHttpSuccessResponse(response);
    },
    (error) => {
      decrementHttpCounter();
      return httpServiceConfigurator.prepareHttpErrorResponse(error);
    }
  );

  return httpServiceConfigurator.createInterface(httpServiceInstance);
};
